/**
 * Append **Teach behavior** quick-log rows to a teacher-owned Google Sheet
 * (same OAuth scopes pattern as the contact log).
 */
import { parseSheetId } from './contactLogSheets';

const SHEETS_BASE = 'https://sheets.googleapis.com/v4/spreadsheets';

export { parseSheetId as parseBehaviorSheetId };

export async function createBehaviorLogSheet(
  accessToken: string,
  title: string = 'DoneGrading Behavior Log'
): Promise<string> {
  const res = await fetch(`${SHEETS_BASE}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      properties: { title },
      sheets: [
        {
          properties: { title: 'Behavior Log', gridProperties: { frozenRowCount: 1 } },
          data: [
            {
              startRow: 0,
              startColumn: 0,
              rowData: [
                {
                  values: [
                    { userEnteredValue: { stringValue: 'Timestamp' } },
                    { userEnteredValue: { stringValue: 'Course' } },
                    { userEnteredValue: { stringValue: 'Assignment' } },
                    { userEnteredValue: { stringValue: 'Student' } },
                    { userEnteredValue: { stringValue: 'Sentiment' } },
                  ],
                },
              ],
            },
          ],
        },
      ],
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { error?: { message?: string } }).error?.message || `HTTP ${res.status}`
    );
  }
  const data = (await res.json()) as { spreadsheetId?: string };
  if (!data.spreadsheetId) throw new Error('No spreadsheet id returned');
  return data.spreadsheetId;
}

async function getFirstSheetName(accessToken: string, spreadsheetId: string): Promise<string> {
  const res = await fetch(`${SHEETS_BASE}/${spreadsheetId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Invalid or inaccessible spreadsheet.');
  const data = (await res.json()) as { sheets?: Array<{ properties?: { title?: string } }> };
  return data.sheets?.[0]?.properties?.title || 'Sheet1';
}

export async function appendBehaviorLogRow(
  accessToken: string,
  spreadsheetId: string,
  row: {
    courseName: string;
    assignmentTitle: string;
    studentName: string;
    sentiment: string;
  }
): Promise<void> {
  const id = parseSheetId(spreadsheetId) || spreadsheetId;
  const firstSheetName = await getFirstSheetName(accessToken, id);
  const timestamp = new Date().toISOString();
  const values = [[timestamp, row.courseName, row.assignmentTitle, row.studentName, row.sentiment]];
  const rangeA1 = `'${firstSheetName.replace(/'/g, "''")}'!A:E`;
  const range = encodeURIComponent(rangeA1);
  const url = `${SHEETS_BASE}/${id}/values/${range}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ values }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { error?: { message?: string } }).error?.message || `HTTP ${res.status}`
    );
  }
}
